import { useCallback } from "react";
import { useAppSelector } from "@/app/hooks";
import { selectFilters } from "@/features/bughurd/boardSelector";

export const useBoardFilters = () => {
  const filters = useAppSelector(selectFilters);
  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    selectedCreatedBy,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
  } = filters;

  const getFilterForGroupBy = useCallback(
    (groupByType: string) => {
      switch (groupByType) {
        case "Status":
          return selectedStatuses;
        case "Assignee":
          return selectedAvatars;
        case "Tags":
          return selectedTags;
        case "Priority":
          return selectedPriorities;
        case "Project":
          return selectedProjects;
        default:
          return [];
      }
    },
    [
      selectedStatuses,
      selectedAvatars,
      selectedTags,
      selectedPriorities,
      selectedProjects,
    ]
  );

  const shouldApplyFiltersForGroupBy = useCallback(
    (groupByType: string) => {
      const relevantFilters = getFilterForGroupBy(groupByType);
      return relevantFilters.length > 0;
    },
    [getFilterForGroupBy]
  );

  const createFiltersObject = useCallback(
    (groupByType: string) => {
      return {
        selectedStatuses: groupByType === "Status" ? selectedStatuses : [],
        selectedAvatars: groupByType === "Assignee" ? selectedAvatars : [],
        selectedTags: groupByType === "Tags" ? selectedTags : [],
        selectedPriorities:
          groupByType === "Priority" ? selectedPriorities : [],
        selectedProjects: groupByType === "Project" ? selectedProjects : [],
      };
    },
    [
      selectedStatuses,
      selectedAvatars,
      selectedTags,
      selectedPriorities,
      selectedProjects,
    ]
  );

  // Helper function to check if any date filters are active
  const hasActiveDateFilters = useCallback(() => {
    return !!(selectedDueDate || selectedStartDate || selectedUpdatedDate);
  }, [selectedDueDate, selectedStartDate, selectedUpdatedDate]);

  // Helper function to get all active date filter parameters
  const getActiveDateFilters = useCallback(() => {
    const dateFilters: any = {};

    if (selectedDueDate) {
      dateFilters.selectedDueDate = selectedDueDate;
      if (selectedDueDate === "date_range") {
        dateFilters.dueDateStart = dueDateStart;
        dateFilters.dueDateEnd = dueDateEnd;
      } else if (selectedDueDate === "no_date") {
        dateFilters.dueDateStart = null;
        dateFilters.dueDateEnd = null;
      }
    }

    if (selectedStartDate) {
      dateFilters.selectedStartDate = selectedStartDate;
      if (selectedStartDate === "date_range") {
        dateFilters.startDateStart = startDateStart;
        dateFilters.startDateEnd = startDateEnd;
      } else if (selectedStartDate === "no_date") {
        dateFilters.startDateStart = null;
        dateFilters.startDateEnd = null;
      }
    }

    if (selectedUpdatedDate) {
      dateFilters.selectedUpdatedDate = selectedUpdatedDate;
      if (selectedUpdatedDate === "date_range") {
        dateFilters.updatedAtStart = updatedAtStart;
        dateFilters.updatedAtEnd = updatedAtEnd;
      }
    }

    return dateFilters;
  }, [
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
  ]);

  // Helper function to check if any filters are active
  const hasActiveFilters = useCallback(() => {
    return !!(
      selectedTags.length > 0 ||
      selectedAvatars.length > 0 ||
      selectedStatuses.length > 0 ||
      selectedPriorities.length > 0 ||
      selectedCreatedBy.length > 0 ||
      hasActiveDateFilters() ||
      isRecurring
    );
  }, [
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedCreatedBy,
    hasActiveDateFilters,
    isRecurring,
  ]);

  return {
    filters,
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    selectedCreatedBy,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
    getFilterForGroupBy,
    shouldApplyFiltersForGroupBy,
    createFiltersObject,
    hasActiveDateFilters,
    getActiveDateFilters,
    hasActiveFilters,
  };
};
